import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUserLoginToken, getUsers } from "../api";
import { StoreProvider } from "../components/ContextProvider";
import Layout from "../components/Layout";
import Spinner from "../components/Spinner";

const AllUsers = () => {
	const { token } = useContext(StoreProvider);
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [meta, setMeta] = useState({});
	const [search, setSearch] = useState("");

	const limit = 12;

	const loadUser = useCallback(async (currentPage = 1, searchTerm = "") => {
		setLoading(true);
		const res = await getUsers("", token, currentPage, limit, searchTerm);
		if (!res?.isError) {
			setUsers(res?.users);
			setMeta(res?.meta || {});
		}
		setLoading(false);
	}, [token]);

	const handleSearch = (e) => {
		setSearch(e.target.value);
		setPage(1);
	};

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			loadUser(page, search);
		}, 600);

		return () => clearTimeout(delayDebounceFn);
	}, [page, search, loadUser]);

	const handlePageChange = (newPage) => {
		if (newPage >= 1 && meta && newPage <= meta.last_page) {
			setPage(newPage);
		}
	};

	const renderPagination = () => {
		if (!meta || meta.total <= limit) return null;

		const lastPage = meta.last_page;
		const pages = [];

		if (lastPage <= 7) {
			for (let i = 1; i <= lastPage; i++) {
				pages.push(i);
			}
		} else {
			// Always show page 1
			pages.push(1);

			if (page <= 4) {
				// Near the start
				pages.push(2, 3, 4, 5);
				pages.push("...");
				pages.push(lastPage);
			} else if (page >= lastPage - 3) {
				// Near the end
				pages.push("...");
				pages.push(lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1);
				pages.push(lastPage);
			} else {
				// In the middle
				pages.push("...");
				pages.push(page - 1, page, page + 1);
				pages.push("...");
				pages.push(lastPage);
			}
		}

		return (
			<nav aria-label="Page navigation" className="mt-5 mb-5">
				<ul className="pagination justify-content-center">
					<li className={`page-item ${page === 1 ? "disabled" : ""}`}>
						<button
							className="page-link"
							style={{ cursor: "pointer" }}
							onClick={() => handlePageChange(page - 1)}
						>
							Prev
						</button>
					</li>
					{pages.map((p, index) => (
						<li
							key={index}
							className={`page-item ${page === p ? "active" : ""} ${
								p === "..." ? "disabled" : ""
							}`}
						>
							{p === "..." ? (
								<span className="page-link">...</span>
							) : (
								<button
									className="page-link"
									style={{ cursor: "pointer" }}
									onClick={() => handlePageChange(p)}
								>
									{p}
								</button>
							)}
						</li>
					))}
					<li className={`page-item ${page === lastPage ? "disabled" : ""}`}>
						<button
							className="page-link"
							style={{ cursor: "pointer" }}
							onClick={() => handlePageChange(page + 1)}
						>
							Next
						</button>
					</li>
				</ul>
			</nav>
		);
	};

	const handleLogin = async (id) => {
		try {
			const res = await getUserLoginToken(id, token);
			if (res?.token) {
				const frontEndUrl = process.env.REACT_APP_MAIN_SITE_URL || "http://localhost:3000";
				const url = `${frontEndUrl}/admin-auth?token=${res.token}`;
				window.open(url, "_blank");
			}
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<Layout>
			<div className="mt-3 mb-3 d-flex justify-content-between">
				<h2 className="card-title">All Users</h2>
				<div>
					<input
						onChange={handleSearch}
						value={search}
						type="search"
						className="form-control"
						placeholder="Search user by email or name"
					/>
				</div>
			</div>
			<div className="d-flex" style={{ flexWrap: "wrap", justifyContent: "center" }}>
				{loading ? (
					<div className="mt-5">
						<Spinner />
					</div>
				) : (
					users.map((user) => (
					<div
						key={user?.id}
						className="card ms-4 me-4 mt-5 mb-3"
						style={{ width: "20rem" }}
					>
						<div className="card-body">
							<h5 title={user?.email} className="card-title">
								{user?.email?.length > 20
									? `${user?.email?.slice(0, 20)}...`
									: user?.email}
							</h5>
							<h6 title={user?.name} className="card-title">
								Name:{" "}
								<b>
									{user?.name?.length > 24
										? `${user?.name?.slice(0, 24)}...`
										: user?.name}
								</b>
							</h6>
							<h6 title={user?.first_name} className="card-title">
								First Name:{" "}
								<b>
									{user?.first_name?.length > 15
										? `${user?.first_name?.slice(0, 15)}...`
										: user?.first_name}
								</b>
							</h6>
							<h6 title={user?.last_name} className="card-title">
								Last Name:{" "}
								<b>
									{user?.last_name?.length > 15
										? `${user?.last_name?.slice(0, 15)}...`
										: user?.last_name}
								</b>
							</h6>
							<h6 className="card-title">
								Total Products: <b>{user?.my_products?.length}</b>
							</h6>
							<h6 className="card-title">
								Plan:{" "}
								<b className="text-capitalize">
									{user?.my_products?.find(
										(p) => p?.product_id === "All-Access"
									)?.status || "N/A"}
								</b>
							</h6>
							<div className="d-flex flex-wrap gap-2 mt-4">
								<button
									onClick={() => handleLogin(user?.id)}
									className="btn btn-primary btn-sm flex-grow-1"
								>
									Login
								</button>
								<Link title="Transaction" to={`/user/transaction/${user?.id}`} className="flex-grow-1">
									<button className="btn btn-success btn-sm w-100">
										Transaction
									</button>
								</Link>
								<Link title="Detail" to={`/user/detail/${user?.id}`} className="flex-grow-1">
									<button className="btn btn-info btn-sm w-100 text-white">
										Detail
									</button>
								</Link>
								<Link title="Edit" to={`/user/edit/${user?.id}`} className="flex-grow-1">
									<button className="btn btn-warning btn-sm w-100 text-white">Edit</button>
								</Link>
							</div>
						</div>
						</div>
					))
				)}
			</div>
			{!loading && renderPagination()}
		</Layout>
	);
};

export default AllUsers;
